import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import Link from 'next/link';
import {
  Box,
  Stack,
  Collapse,
  useBreakpoint,
  Flex,
  HStack,
  VStack,
  Center,
} from '@chakra-ui/react';
import { Button, IconButton } from '@outdoorly/button';
import { Card } from '@outdoorly/card';
import { Text } from '@outdoorly/text';

import {
  PackageIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CreditCardIcon,
  DollarSignIcon,
  StarIcon,
  UserIcon,
  TruckIcon,
  ChevronRightIcon,
  BadgeCheckIcon,
} from '@outdoorly/icons';
import propTypes from 'prop-types';
import { PageLayout } from '../page-layout';
import { useAuth } from '../../providers/auth';
import { NotificationBadge } from '@outdoorly/notification-badge';
import { useQualification } from '../../providers/qualifications';

const items = [
  {
    name: 'Account Settings',
    pathname: '/profile',
    Icon: UserIcon,
  },
  {
    name: 'Payment methods',
    pathname: '/profile/payment-methods',
    Icon: CreditCardIcon,
  },
  {
    name: 'Shipping addresses',
    pathname: '/profile/addresses',
    Icon: TruckIcon,
  },
  {
    name: 'Account credits',
    pathname: '/profile/credits',
    Icon: DollarSignIcon,
  },
  {
    name: 'Orders',
    pathname: '/profile/orders',
    Icon: PackageIcon,
  },
  // {
  //   name: 'Likes',
  //   pathname: '/profile/likes',
  //   Icon: BookmarkIcon,
  // },
  {
    name: 'Reviews',
    pathname: '/profile/reviews',
    Icon: StarIcon,
  },
  {
    name: 'Qualifications',
    pathname: '/profile/qualifications',
    Icon: BadgeCheckIcon,
  },
];

const MenuItem = ({ name, url, Icon, isActive, includeNotification }) => {
  return (
    <Box w="full" as="li" listStyleType="none">
      <Link href={url} passHref>
        <Flex
          as="a"
          align="center"
          justify="space-between"
          backgroundColor={isActive ? 'scheme.bgSecondary' : 'none'}
          cursor="pointer"
          padding="2"
          borderRadius="md"
          _hover={{
            backgroundColor: 'scheme.bgSecondary',
          }}
        >
          <HStack align="center" spacing="2">
            <NotificationBadge size="sm" isBadgeVisible={includeNotification}>
              <Icon height="6" width="6" color="scheme.textSecondary" />
            </NotificationBadge>
            <Text size="lg">{name}</Text>
          </HStack>

          <ChevronRightIcon stroke="scheme.textPrimary" />
        </Flex>
      </Link>
    </Box>
  );
};

MenuItem.propTypes = {
  isActive: propTypes.bool,
  name: propTypes.string,
  url: propTypes.string,
  color: propTypes.string,
  Icon: propTypes.element,
  includeNotification: propTypes.bool,
};

export const Menu = () => {
  const router = useRouter();

  const { hasNoQualificationsOrOpenApplications } = useQualification();

  return (
    <VStack spacing="1" paddingX="4" marginY="4" align="start" w="full">
      {items.map(({ pathname, ...item }) => (
        <MenuItem
          key={pathname}
          isActive={router.pathname === pathname}
          url={pathname}
          includeNotification={
            item.name === 'Qualifications' &&
            hasNoQualificationsOrOpenApplications
          }
          {...item}
        />
      ))}
    </VStack>
  );
};

export const ProfileLayout = ({ children, pageTitle }) => {
  const { logout } = useAuth();
  const [isMenuOpen, setMenuOpen] = useState(true);
  const breakpoint = useBreakpoint('sm');

  useEffect(() => {
    setMenuOpen(!['base', 'sm'].includes(breakpoint));
  }, [breakpoint, setMenuOpen]);

  return (
    <PageLayout backgroundColor="scheme.bgSecondary">
      <Stack
        width="full"
        spacing={6}
        flexGrow={1}
        maxWidth="page"
        marginX="auto"
        paddingBottom="gutter"
        paddingTop={['none', null, 'gutter']}
        paddingX={['none', null, 'gutter']}
      >
        <Flex
          direction="row"
          display="grid"
          gridTemplateColumns={['1fr', '1fr', '275px 1fr']}
          gridGap="gutter"
          marginLeft={[null, null, null, null, -100]}
        >
          <Box>
            <Card
              boxShadow="sm"
              padding={0}
              borderRadius={['none', 'none', 'xl']}
            >
              {['base', 'sm'].includes(breakpoint) ? (
                <>
                  <Box
                    width="100%"
                    justify="space-between"
                    padding="gutter"
                    verticalAlign="middle"
                    position="relative"
                    textAlign="center"
                  >
                    <Text size="lg" fontWeight="medium" verticalAlign="middle">
                      {pageTitle}
                    </Text>
                    <IconButton
                      position="absolute"
                      right="1"
                      top="50%"
                      transform="translateY(-50%)"
                      variant="ghost"
                      verticalAlign="middle"
                      onClick={() => setMenuOpen(!isMenuOpen)}
                      icon={
                        isMenuOpen ? <ChevronUpIcon /> : <ChevronDownIcon />
                      }
                    />
                  </Box>
                  <Collapse in={isMenuOpen}>
                    <Menu />
                    <Center p={4} onClick={logout}>
                      <Button variant="link">Sign out</Button>
                    </Center>
                  </Collapse>
                </>
              ) : (
                <Flex direction="column">
                  <Menu />
                </Flex>
              )}
            </Card>
            {!['base', 'sm'].includes(breakpoint) && (
              <Center p={4} onClick={logout}>
                <Button variant="link">Sign out</Button>
              </Center>
            )}
          </Box>
          <Box paddingX={['gutter', null, 'none']}>{children}</Box>
        </Flex>
      </Stack>
    </PageLayout>
  );
};

ProfileLayout.propTypes = {
  children: propTypes.node,
  pageTitle: propTypes.string.isRequired,
};
